import React, { useState } from 'react';

const InfoIcon = () => (
  <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="9" stroke="#3B82F6" strokeWidth="1.5" />
    <path
      d="M10 9V14"
      stroke="#3B82F6"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <circle cx="10" cy="6.5" r="1" fill="#3B82F6" />
  </svg>
);

const CheckCircle = ({ color = '#10B981' }) => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <circle
      cx="9"
      cy="9"
      r="8"
      stroke={color}
      strokeWidth="1.5"
      opacity="0.3"
    />
    <path
      d="M6 9L8.5 11.5L12.5 6.5"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AlertTriangle = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path
      d="M9 2L16.5 15H1.5L9 2Z"
      stroke="#F59E0B"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
    <path
      d="M9 7V10.5"
      stroke="#F59E0B"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="9" cy="12.5" r="0.75" fill="#F59E0B" />
  </svg>
);

const ChevronDown = ({ open }: { open: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    style={{
      transition: 'transform 0.2s ease',
      transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
    }}
  >
    <path
      d="M4 6L8 10L12 6"
      stroke="#64748B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M6 6L14 14M14 6L6 14"
      stroke="#94A3B8"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

export interface RepostCampaignModalProps {
  platform: 'instagram' | 'tiktok';
  open: boolean;
  onAccept: (dontShowAgain: boolean) => void;
  onCancel: () => void;
}

export const RepostCampaignModal: React.FC<RepostCampaignModalProps> = ({
  platform = 'instagram',
  open,
  onAccept,
  onCancel
}) => {
  const [expanded, setExpanded] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  if (!open) return null;

  const platformName = platform === 'tiktok' ? 'TikTok' : 'Instagram';

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onCancel}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15,23,42,0.5)',
          backdropFilter: 'blur(4px)',
          zIndex: 999,
          animation: 'repostFadeIn 0.2s ease'
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          width: '100%',
          maxWidth: 480,
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 1px 3px rgba(0,0,0,0.08)',
          fontFamily: "'DM Sans', sans-serif",
          animation: 'repostSlideUp 0.25s ease',
          overflow: 'hidden'
        }}
      >
        {/* Header — icon + title row */}
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            padding: '20px 20px 0'
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: '#EFF6FF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <InfoIcon />
          </div>
          <p
            style={{
              fontSize: 17,
              fontWeight: 700,
              color: '#1E293B',
              margin: 0,
              flex: 1
            }}
          >
            Campaña de repost en {platformName}
          </p>
          <button
            onClick={onCancel}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              borderRadius: 6,
              flexShrink: 0
            }}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Subtitle */}
        <p
          style={{
            fontSize: 13,
            color: '#334155',
            margin: 0,
            lineHeight: 1.5,
            padding: '8px 20px 0'
          }}
        >
          Antes de continuar, ten en cuenta las siguientes consideraciones sobre
          este tipo de campaña.
        </p>

        {/* Limitations */}
        <div
          style={{
            padding: '16px 20px 0 72px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10
          }}
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <div style={{ flexShrink: 0, marginTop: 1 }}>
              <AlertTriangle />
            </div>
            <p
              style={{
                fontSize: 13,
                color: '#475569',
                margin: 0,
                lineHeight: 1.5
              }}
            >
              <span style={{ fontWeight: 600, color: '#1E293B' }}>
                Acción manual del embajador:{' '}
              </span>
              el repost depende completamente de que el embajador siga las
              instrucciones y complete la acción en {platformName}.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <div style={{ flexShrink: 0, marginTop: 1 }}>
              <AlertTriangle />
            </div>
            <p
              style={{
                fontSize: 13,
                color: '#475569',
                margin: 0,
                lineHeight: 1.5
              }}
            >
              <span style={{ fontWeight: 600, color: '#1E293B' }}>
                Sin verificación ni medición:{' '}
              </span>
              no es posible confirmar automáticamente si el embajador realizó el
              repost ni medir su alcance o engagement.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <div style={{ flexShrink: 0, marginTop: 1 }}>
              <AlertTriangle />
            </div>
            <p
              style={{
                fontSize: 13,
                color: '#475569',
                margin: 0,
                lineHeight: 1.5
              }}
            >
              <span style={{ fontWeight: 600, color: '#1E293B' }}>
                Sin texto descriptivo:{' '}
              </span>
              este tipo de campaña no permite agregar texto personalizado a la
              publicación del embajador. El repost de esta red social no permite
              incluir textos.
            </p>
          </div>
        </div>

        {/* Expandable — what we CAN do */}
        <div
          style={{ margin: '14px 20px 0 72px', borderTop: '1px solid #F1F5F9' }}
        >
          <button
            onClick={() => setExpanded(!expanded)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 0',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif"
            }}
          >
            <span style={{ fontSize: 12.5, fontWeight: 600, color: '#3B82F6' }}>
              ¿Qué sí podemos hacer?
            </span>
            <ChevronDown open={expanded} />
          </button>

          {expanded && (
            <div
              style={{
                paddingBottom: 4,
                display: 'flex',
                flexDirection: 'column',
                gap: 7
              }}
            >
              {[
                'Registrar que el embajador abrió el enlace de la campaña',
                'Mostrar instrucciones paso a paso para completar el repost',
                'Contabilizar la participación del embajador en la campaña'
              ].map((text, i) => (
                <div
                  key={i}
                  style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}
                >
                  <div style={{ flexShrink: 0, marginTop: 1 }}>
                    <CheckCircle />
                  </div>
                  <p
                    style={{
                      fontSize: 12.5,
                      color: '#475569',
                      margin: 0,
                      lineHeight: 1.5
                    }}
                  >
                    {text}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 20px',
            marginTop: 6,
            background: '#F8FAFC',
            borderTop: '1px solid #F1F5F9',
            display: 'flex',
            flexDirection: 'column',
            gap: 14
          }}
        >
          {/* Don't show again */}
          <label
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <div
              onClick={e => {
                e.preventDefault();
                setDontShowAgain(!dontShowAgain);
              }}
              style={{
                width: 18,
                height: 18,
                borderRadius: 4,
                flexShrink: 0,
                border: dontShowAgain ? 'none' : '1.5px solid #CBD5E1',
                background: dontShowAgain ? '#3B82F6' : '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s ease',
                cursor: 'pointer'
              }}
            >
              {dontShowAgain && (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M3 6L5.5 8.5L9.5 3.5"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            <span style={{ fontSize: 12.5, color: '#334155' }}>
              No mostrar este mensaje de nuevo
            </span>
          </label>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              onClick={onCancel}
              style={{
                padding: '10px 20px',
                borderRadius: 10,
                border: '1.5px solid #E2E8F0',
                background: '#fff',
                color: '#475569',
                fontSize: 13.5,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif",
                transition: 'all 0.15s ease'
              }}
            >
              Cancelar
            </button>
            <button
              onClick={() => onAccept(dontShowAgain)}
              style={{
                padding: '10px 24px',
                borderRadius: 10,
                border: 'none',
                background: 'linear-gradient(135deg, #4F7DF7, #7B5CF5)',
                color: '#fff',
                fontSize: 13.5,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif",
                boxShadow: '0 2px 8px rgba(79,125,247,0.3)',
                transition: 'all 0.15s ease'
              }}
            >
              Continuar
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes repostFadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes repostSlideUp { from { opacity: 0; transform: translate(-50%, -46%) } to { opacity: 1; transform: translate(-50%, -50%) } }
      `}</style>
    </>
  );
};
